import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';

const LegacyUrlHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();

  useEffect(() => {
    const path = location.pathname;
    
    // Handle legacy URLs that don't match current structure
    const legacyRedirects: Record<string, string> = {
      // Old language-only URLs
      '/ar': '/ar-eg/cairo',
      '/en': '/en-eg/cairo',
      
      // Old URLs without city prefix
      '/ar/auth': '/ar-eg/cairo/auth',
      '/en/auth': '/en-eg/cairo/auth',
      '/ar/products': '/ar-eg/cairo/products',
      '/en/products': '/en-eg/cairo/products',
      '/ar/category': '/ar-eg/cairo/category',
      '/en/category': '/en-eg/cairo/category',
      '/ar/occasion': '/ar-eg/cairo/occasion',
      '/en/occasion': '/en-eg/cairo/occasion',
      '/ar/about': '/ar-eg/cairo/about',
      '/en/about': '/en-eg/cairo/about',
      '/ar/contact': '/ar-eg/cairo/contact',
      '/en/contact': '/en-eg/cairo/contact',
      '/ar/faq': '/ar-eg/cairo/faq',
      '/en/faq': '/en-eg/cairo/faq',
      '/ar/privacy': '/ar-eg/cairo/privacy',
      '/en/privacy': '/en-eg/cairo/privacy',
      '/ar/terms': '/ar-eg/cairo/terms',
      '/en/terms': '/en-eg/cairo/terms',
      '/ar/returns': '/ar-eg/cairo/returns',
      '/en/returns': '/en-eg/cairo/returns',
      '/ar/shipping': '/ar-eg/cairo/shipping',
      '/en/shipping': '/en-eg/cairo/shipping',
      '/ar/checkout': '/ar-eg/cairo/checkout',
      '/en/checkout': '/en-eg/cairo/checkout',
      '/ar/profile': '/ar-eg/cairo/profile',
      '/en/profile': '/en-eg/cairo/profile',
      '/ar/my-orders': '/ar-eg/cairo/my-orders',
      '/en/my-orders': '/en-eg/cairo/my-orders',
      '/ar/track-order': '/ar-eg/cairo/track-order',
      '/en/track-order': '/en-eg/cairo/track-order',
      '/ar/flowers': '/ar-eg/cairo/flowers',
      '/en/flowers': '/en-eg/cairo/flowers',
      '/ar/tips': '/ar-eg/cairo/tips',
      '/en/tips': '/en-eg/cairo/tips',
      '/ar/holiday': '/ar-eg/cairo/holiday',
      '/en/holiday': '/en-eg/cairo/holiday',
      '/ar/search': '/ar-eg/cairo/search',
      '/en/search': '/en-eg/cairo/search',
      '/ar/sitemap': '/ar-eg/cairo/sitemap',
      '/en/sitemap': '/en-eg/cairo/sitemap',
    };

    // Check if current path matches any legacy redirect
    if (legacyRedirects[path]) {
      const newPath = legacyRedirects[path];
      navigate(newPath, { replace: true });
      return;
    }

    // Handle legacy URLs with parameters
    const legacyPatterns = [
      // /ar/category/something → /ar-eg/cairo/category/something
      { pattern: /^\/ar\/category\/(.+)$/, replacement: '/ar-eg/cairo/category/$1' },
      { pattern: /^\/en\/category\/(.+)$/, replacement: '/en-eg/cairo/category/$1' },
      
      // /ar/occasion/something → /ar-eg/cairo/occasion/something
      { pattern: /^\/ar\/occasion\/(.+)$/, replacement: '/ar-eg/cairo/occasion/$1' },
      { pattern: /^\/en\/occasion\/(.+)$/, replacement: '/en-eg/cairo/occasion/$1' },
      
      // /ar/products/something → /ar-eg/cairo/products/something
      { pattern: /^\/ar\/products\/(.+)$/, replacement: '/ar-eg/cairo/products/$1' },
      { pattern: /^\/en\/products\/(.+)$/, replacement: '/en-eg/cairo/products/$1' },
      
      // /ar/tips/something → /ar-eg/cairo/tips/something
      { pattern: /^\/ar\/tips\/(.+)$/, replacement: '/ar-eg/cairo/tips/$1' },
      { pattern: /^\/en\/tips\/(.+)$/, replacement: '/en-eg/cairo/tips/$1' },
      
      // /ar/holiday/something → /ar-eg/cairo/holiday/something
      { pattern: /^\/ar\/holiday\/(.+)$/, replacement: '/ar-eg/cairo/holiday/$1' },
      { pattern: /^\/en\/holiday\/(.+)$/, replacement: '/en-eg/cairo/holiday/$1' },
      
      // /ar/order/something → /ar-eg/cairo/order/something
      { pattern: /^\/ar\/order\/(.+)$/, replacement: '/ar-eg/cairo/order/$1' },
      { pattern: /^\/en\/order\/(.+)$/, replacement: '/en-eg/cairo/order/$1' },
      
      // /ar/order-confirmation/something → /ar-eg/cairo/order-confirmation/something
      { pattern: /^\/ar\/order-confirmation\/(.+)$/, replacement: '/ar-eg/cairo/order-confirmation/$1' },
      { pattern: /^\/en\/order-confirmation\/(.+)$/, replacement: '/en-eg/cairo/order-confirmation/$1' },
      
      // /ar/area/something → /ar-eg/area/something
      { pattern: /^\/ar\/area\/(.+)$/, replacement: '/ar-eg/area/$1' },
      { pattern: /^\/en\/area\/(.+)$/, replacement: '/en-eg/area/$1' },

      // Bare /area/something (no language prefix) → /en-eg/area/something.
      // These legacy URLs were indexed by Google and render "Area not found"
      // via the mis-mapped bare /area/:citySlug route. Client-side safety net
      // for the server-side 301 in public/_redirects. Matches /area/X and
      // /area/X/Y. Excludes /area/products which has its own redirect rule.
      { pattern: /^\/area\/(?!products)(.+)$/, replacement: '/en-eg/area/$1' },
    ];

    for (const { pattern, replacement } of legacyPatterns) {
      if (pattern.test(path)) {
        const newPath = path.replace(pattern, replacement);
        navigate(newPath, { replace: true });
        return;
      }
    }

  }, [location.pathname, navigate]);

  return null; // This component doesn't render anything
};

export default LegacyUrlHandler;
